import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, ReferenceLine, LabelList,
} from 'recharts';
import {
  Scale, TrendingUp, TrendingDown, Plus, Trash2, Save, Lightbulb, AlertTriangle, Target, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCurrency = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const LEVEL_STYLES = {
  danger: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};
const ICON_COLORS = {
  danger: 'text-red-600', warning: 'text-amber-600',
  success: 'text-emerald-600', info: 'text-blue-600',
};
const ICON_FOR_LEVEL = {
  danger: AlertTriangle, warning: AlertTriangle, success: TrendingUp, info: Lightbulb,
};

export default function CashFlowAnalysisCard({ BACKEND_URL, view, year, month, rangeStart, rangeEnd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [liabilityRows, setLiabilityRows] = useState([{ label: '', amount: '' }]);
  const [ongoing, setOngoing] = useState('');
  const [invoiced, setInvoiced] = useState('');
  const [bankBalance, setBankBalance] = useState('');
  const [notes, setNotes] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (view === 'range') {
        params.append('view', 'range');
        params.append('start_date', rangeStart);
        params.append('end_date', rangeEnd);
      } else {
        params.append('view', 'monthly');
        params.append('year', year);
        params.append('month', month);
      }
      const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-flow-analysis?${params}`,
        { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        // Hydrate manual inputs
        const rows = (json.manual.liability_breakdown || []).map((r) => ({
          label: r.label || '', amount: r.amount?.toString() || ''
        }));
        setLiabilityRows(rows.length ? rows : [{ label: '', amount: '' }]);
        setOngoing((json.manual.ongoing_jobs_value || 0).toString());
        setInvoiced((json.manual.invoiced_jobs_value || 0).toString());
        // null / undefined ⇒ empty string so the input shows its placeholder
        setBankBalance(json.manual.bank_balance == null ? '' : String(json.manual.bank_balance));
        setNotes(json.manual.notes || '');
      }
    } catch (e) {
      console.error('Failed to load cash flow:', e);
    } finally { setLoading(false); }
  }, [BACKEND_URL, view, year, month, rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = liabilityRows
        .map((r) => ({ label: r.label.trim(), amount: parseFloat(r.amount) || 0 }))
        .filter((r) => r.label || r.amount > 0);
      const sumLiabilities = cleaned.reduce((sum, r) => sum + (r.amount || 0), 0);
      const body = {
        start_date: data?.start_date,
        end_date: data?.end_date,
        current_liabilities: sumLiabilities,
        ongoing_jobs_value: parseFloat(ongoing) || 0,
        invoiced_jobs_value: parseFloat(invoiced) || 0,
        // Only send bank_balance if SM actually typed something. Empty
        // string ⇒ skip (server keeps prior value or stays None).
        ...(bankBalance.trim() !== '' && { bank_balance: parseFloat(bankBalance) || 0 }),
        liability_breakdown: cleaned,
        notes,
      };
      const res = await fetch(`${BACKEND_URL}/api/super-admin/cash-flow-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success('Cash flow snapshot saved'); fetchData(); }
      else toast.error('Failed to save');
    } catch (_e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateRow = (idx, field, val) =>
    setLiabilityRows((rows) => rows.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const addRow = () => setLiabilityRows((rows) => [...rows, { label: '', amount: '' }]);
  const removeRow = (idx) => setLiabilityRows((rows) => rows.filter((_, i) => i !== idx));

  const liabSum = useMemo(
    () => liabilityRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [liabilityRows]
  );

  const derived = data?.derived || {};
  const cashInHand = Number(derived.cash_in_hand_remaining || 0);
  const enteredBank = bankBalance.trim() === '' ? 0 : (parseFloat(bankBalance) || 0);
  const totalHoldings = enteredBank + cashInHand
    + (parseFloat(ongoing) || 0) + (parseFloat(invoiced) || 0);
  const netPosition = totalHoldings - liabSum;
  const isPositive = netPosition >= 0;

  // Bar chart data: holdings (left, green) vs liabilities (right, red).
  // Banking auto-pull is GONE per user request — replaced with manual
  // Bank Balance + cash-in-hand-remaining from Cash Incentives.
  const balanceChartData = [
    { name: 'Bank Balance', amount: enteredBank, group: 'asset' },
    { name: 'Cash Remaining', amount: cashInHand, group: 'asset' },
    { name: 'Ongoing Jobs', amount: parseFloat(ongoing) || 0, group: 'asset' },
    { name: 'Invoiced Jobs', amount: parseFloat(invoiced) || 0, group: 'asset' },
    { name: 'Liabilities', amount: -(liabSum), group: 'liability' },
  ];

  if (loading && !data) {
    return (
      <Card className="p-6"><div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div></Card>
    );
  }

  return (
    <Card className="p-6" data-testid="cash-flow-card">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Company Cash-Flow Position
          </h3>
          <p className="text-sm text-slate-500">
            {data?.start_date} → {data?.end_date} • Bank balance & WIP entered manually · Cash remaining pulled live from Cash Incentives
          </p>
        </div>
        <div className={`px-4 py-2 rounded-lg border-2 ${isPositive ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
          <div className="flex items-center gap-2">
            {isPositive ? <TrendingUp className="h-5 w-5 text-emerald-700" /> : <TrendingDown className="h-5 w-5 text-red-700" />}
            <div>
              <p className={`text-xs uppercase tracking-wide ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                {isPositive ? 'Cash-Flow Positive' : 'Cash-Flow Negative'}
              </p>
              <p className={`text-xl font-bold ${isPositive ? 'text-emerald-900' : 'text-red-900'}`} data-testid="cf-net-position">
                {isPositive ? '' : '−'}{fmtCurrency(Math.abs(netPosition))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Visual: Balance scale */}
      <div className="mb-6 p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg border">
        <div className="flex items-stretch gap-4">
          {/* Holdings stack */}
          <div className="flex-1">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Holdings — {fmtCurrency(totalHoldings)}</p>
            <div className="space-y-1">
              {[
                { label: 'Bank Balance', value: enteredBank, color: 'bg-emerald-500', hint: bankBalance.trim() === '' ? 'not entered' : null },
                { label: 'Cash Remaining', value: cashInHand, color: 'bg-emerald-400', hint: 'live from Cash Incentives' },
                { label: 'Ongoing Jobs', value: parseFloat(ongoing) || 0, color: 'bg-blue-400' },
                { label: 'Invoiced Jobs', value: parseFloat(invoiced) || 0, color: 'bg-blue-300' },
              ].map((b) => {
                const pct = totalHoldings > 0 ? Math.max(2, (b.value / totalHoldings) * 100) : 0;
                return (
                  <div key={b.label} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-28" title={b.hint || undefined}>
                      {b.label}
                      {b.hint && <span className="text-[10px] text-slate-400 ml-1">({b.hint})</span>}
                    </span>
                    <div className="flex-1 bg-slate-200 rounded h-5 overflow-hidden">
                      <div className={`${b.color} h-full transition-all`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-20 text-right">{fmtCurrency(b.value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Center scale icon */}
          <div className="flex flex-col items-center justify-center px-2">
            <Scale className={`h-12 w-12 ${isPositive ? 'text-emerald-600' : 'text-red-600'}`} 
                   style={{ transform: `rotate(${isPositive ? '8' : '-8'}deg)` }} />
            <span className={`mt-1 text-xs font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>
              {isPositive ? 'TIPS POSITIVE' : 'TIPS NEGATIVE'}
            </span>
          </div>

          {/* Liabilities stack */}
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Liabilities — {fmtCurrency(liabSum)}</p>
            {liabilityRows.filter((r) => parseFloat(r.amount) > 0).length === 0 ? (
              <p className="text-xs text-slate-500 italic">No liabilities entered yet</p>
            ) : (
              <div className="space-y-1">
                {liabilityRows
                  .filter((r) => parseFloat(r.amount) > 0)
                  .map((r, idx) => {
                    const pct = liabSum > 0 ? Math.max(2, (parseFloat(r.amount) / liabSum) * 100) : 0;
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 w-28 truncate" title={r.label}>{r.label || '(unnamed)'}</span>
                        <div className="flex-1 bg-slate-200 rounded h-5 overflow-hidden">
                          <div className="bg-red-500 h-full transition-all" style={{ width: `${pct}%` }}></div>
                        </div>
                        <span className="text-xs font-medium text-slate-700 w-20 text-right">{fmtCurrency(parseFloat(r.amount))}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bar chart: Net position */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Components Breakdown</h4>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={balanceChartData} layout="vertical" margin={{ left: 30, right: 20, top: 5, bottom: 5 }}>
            <XAxis type="number" tickFormatter={(v) => `$${Math.abs(v).toLocaleString()}`} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
            <Tooltip formatter={(v) => fmtCurrency(Math.abs(v))} />
            <ReferenceLine x={0} stroke="#64748b" />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
              {balanceChartData.map((d, idx) => (
                <Cell key={idx} fill={d.group === 'asset' ? '#10b981' : '#ef4444'} />
              ))}
              <LabelList dataKey="amount" position="right" formatter={(v) => fmtCurrency(Math.abs(v))} style={{ fontSize: 10, fill: '#475569' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Manual entry forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700">Manual: Current Holdings & WIP</h4>
          <div>
            <Label className="text-xs flex items-center gap-1">
              Current Bank Balance ($)
              <span className="text-[10px] text-amber-700 font-normal italic">— enter today's actual balance, not period banking flow</span>
            </Label>
            <Input
              type="number" step="0.01" min="0"
              value={bankBalance}
              onChange={(e) => setBankBalance(e.target.value)}
              placeholder="e.g. 12500"
              data-testid="cf-bank-balance-input"
              className="border-amber-300 focus-visible:ring-amber-200"
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              Cash Remaining (year-to-date · from Cash Incentives)
              <span className="text-[10px] text-slate-500 font-normal italic">— Jan–Dec: cash income − cash expenses − incentives paid</span>
            </Label>
            <div
              className="h-10 px-3 py-2 rounded-md border bg-slate-50 text-sm font-medium text-slate-700 flex items-center justify-between"
              data-testid="cf-cash-in-hand-display"
              title={`YTD cash income $${(derived.cash_in_hand_cumulative || 0).toLocaleString()} − incentives paid $${(derived.incentives_paid_cumulative || 0).toLocaleString()} (mirrors the Cash Remaining figure on the Cash Incentives tab).`}
            >
              <span data-testid="cf-cash-remaining-value">{fmtCurrency(cashInHand)}</span>
              <span className="text-[10px] text-slate-400">auto · YTD</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Sum of Ongoing Jobs ($)</Label>
            <Input type="number" step="0.01" min="0" value={ongoing} onChange={(e) => setOngoing(e.target.value)}
                   placeholder="e.g. 8000" data-testid="cf-ongoing-input" />
          </div>
          <div>
            <Label className="text-xs">Sum of Invoiced (unpaid) Jobs ($)</Label>
            <Input type="number" step="0.01" min="0" value={invoiced} onChange={(e) => setInvoiced(e.target.value)}
                   placeholder="e.g. 5000" data-testid="cf-invoiced-input" />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context..." />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700">Manual: Current Liabilities</h4>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="cf-add-liability">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {liabilityRows.map((r, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input value={r.label} onChange={(e) => updateRow(idx, 'label', e.target.value)}
                       placeholder="Label (e.g. Rent, Wages)" className="flex-1" />
                <Input type="number" step="0.01" min="0" value={r.amount} onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                       placeholder="Amount" className="w-32" />
                <Button size="icon" variant="ghost" onClick={() => removeRow(idx)} disabled={liabilityRows.length <= 1}>
                  <Trash2 className="h-3 w-3 text-red-600" />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600">
            Total liabilities: <strong>{fmtCurrency(liabSum)}</strong>
          </p>
        </div>
      </div>

      <div className="flex justify-end mb-6">
        <Button onClick={handleSave} disabled={saving} data-testid="cf-save-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Snapshot'}
        </Button>
      </div>

      {/* Insights */}
      {data?.insights && data.insights.length > 0 && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            Recommendations to Improve Profit
          </h4>
          <div className="space-y-2">
            {data.insights.map((ins, idx) => {
              const Icon = ICON_FOR_LEVEL[ins.level] || Lightbulb;
              return (
                <div key={idx} className={`p-3 border rounded-lg flex gap-3 ${LEVEL_STYLES[ins.level] || LEVEL_STYLES.info}`} data-testid={`cf-insight-${idx}`}>
                  <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${ICON_COLORS[ins.level] || ICON_COLORS.info}`} />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{ins.title}</p>
                    <p className="text-xs mt-0.5 opacity-90">{ins.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
